// Agenda inteligente (pedido 2026-10-21) — aba "Agenda" do Assistente IA:
// calendário semanal, linha do tempo do dia escolhido e o chat da IA em
// modo compacto embaixo (texto ou voz) pra criar, consultar, editar e
// cancelar compromissos. Dados reais da agenda PESSOAL do cliente
// (GET /ai-assistant/events?from&to) — nada a ver com a Agenda da empresa.
// A conversa daqui é separada da aba Chat e não fica guardada: a resposta
// aparece por cima e some.
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, CalendarPlus, ChevronLeft, ChevronRight, Clock, Loader2, Pencil, Plus, Trash2, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { gpsDriveAiAssistantService, type AiMe, type AiPersonalEvent } from "@/services/gpsdrive-ai-assistant.service";
import { AssistantChat } from "./assistant-chat";
import { CalendarPicker } from "./calendar-picker";
import { EventEditor } from "./event-editor";

const TZ = "America/Sao_Paulo";
const WEEKDAYS = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];

const todayYmd = () => new Date().toLocaleDateString("en-CA", { timeZone: TZ });
const ymdOf = (iso: string) => new Date(iso).toLocaleDateString("en-CA", { timeZone: TZ });
const hhmm = (iso: string) => new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", timeZone: TZ });

// Datas como "YYYY-MM-DD" e contas em UTC (sem fuso no meio).
function addDays(ymd: string, n: number) {
  const d = new Date(`${ymd}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}
function mondayOf(ymd: string) {
  const dow = new Date(`${ymd}T12:00:00Z`).getUTCDay(); // 0 = domingo
  return addDays(ymd, dow === 0 ? -6 : 1 - dow);
}
const dayNum = (ymd: string) => Number(ymd.slice(8, 10));
const ddmm = (ymd: string) => `${ymd.slice(8, 10)}/${ymd.slice(5, 7)}`;
const longDay = (ymd: string) => {
  const s = new Date(`${ymd}T12:00:00Z`).toLocaleDateString("pt-BR", { weekday: "long", day: "numeric", month: "long", timeZone: "UTC" });
  return s.charAt(0).toUpperCase() + s.slice(1);
};

function remindLabel(m: number) {
  if (m === 0) return "na hora";
  if (m % 1440 === 0) return `${m / 1440} dia${m === 1440 ? "" : "s"} antes`;
  if (m % 60 === 0) return `${m / 60} h antes`;
  return `${m} min antes`;
}

function untilLabel(iso: string) {
  const diff = new Date(iso).getTime() - Date.now();
  if (diff <= 0) return "agora";
  const min = Math.round(diff / 60_000);
  if (min < 60) return `em ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `em ${h}h${min % 60 ? ` ${min % 60}min` : ""}`;
  const d = Math.round(h / 24);
  return `em ${d} dia${d > 1 ? "s" : ""}`;
}

export function SmartAgenda({ me, keyboardInset = 0 }: { me: AiMe; keyboardInset?: number }) {
  const qc = useQueryClient();
  const [selected, setSelected] = useState(todayYmd);
  const [weekStart, setWeekStart] = useState(() => mondayOf(todayYmd()));
  // Conversa só na memória (pedido 2026-10-21: "o histórico disso não precisa").
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [prefill, setPrefill] = useState<{ text: string; nonce: number } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  // Agendar pela tela (calendário completo + relógio de rolar).
  const [editor, setEditor] = useState<{ event: AiPersonalEvent | null } | null>(null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(() => todayYmd().slice(0, 7));
  const [, tick] = useState(0);
  const today = todayYmd();
  const weekEnd = addDays(weekStart, 6);

  // Atualiza "em 2h" / marcador "agora" a cada minuto.
  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 60_000);
    return () => clearInterval(t);
  }, []);

  const events = useQuery({
    queryKey: ["ai-assistant", "events", weekStart, weekEnd],
    queryFn: () => gpsDriveAiAssistantService.events({ from: weekStart, to: weekEnd }),
  });
  // Próximo compromisso (de hoje em diante), independente da semana aberta.
  const upcoming = useQuery({
    queryKey: ["ai-assistant", "events", "upcoming"],
    queryFn: () => gpsDriveAiAssistantService.events({ from: today, to: addDays(today, 60) }),
    refetchInterval: 5 * 60_000,
  });
  const next = (upcoming.data ?? []).find((e) => new Date(e.startsAt).getTime() > Date.now());
  // Bolinhas do calendário completo: compromissos do mês que está aberto nele.
  const monthEvents = useQuery({
    queryKey: ["ai-assistant", "events", "month", calendarMonth],
    queryFn: () => {
      const [y, m] = calendarMonth.split("-").map(Number) as [number, number];
      const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
      return gpsDriveAiAssistantService.events({ from: `${calendarMonth}-01`, to: `${calendarMonth}-${String(last).padStart(2, "0")}` });
    },
    enabled: calendarOpen || !!editor,
  });
  const markedDays = useMemo(() => {
    const set = new Set<string>();
    for (const e of [...(monthEvents.data ?? []), ...(events.data ?? [])]) set.add(ymdOf(e.startsAt));
    return set;
  }, [monthEvents.data, events.data]);
  const jumpTo = (d: string) => {
    setSelected(d);
    setWeekStart(mondayOf(d));
  };

  const remove = useMutation({
    mutationFn: (id: string) => gpsDriveAiAssistantService.deleteEvent(id),
    onSuccess: () => {
      setConfirmDelete(null);
      qc.invalidateQueries({ queryKey: ["ai-assistant", "events"] });
    },
  });

  const byDay = useMemo(() => {
    const map = new Map<string, AiPersonalEvent[]>();
    for (const e of events.data ?? []) {
      const k = ymdOf(e.startsAt);
      map.set(k, [...(map.get(k) ?? []), e]);
    }
    return map;
  }, [events.data]);
  const dayEvents = byDay.get(selected) ?? [];
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const monthLabel = (() => {
    const s = new Date(`${selected}T12:00:00Z`).toLocaleDateString("pt-BR", { month: "long", year: "numeric", timeZone: "UTC" });
    return s.charAt(0).toUpperCase() + s.slice(1);
  })();

  const goWeek = (delta: number) => {
    const ws = addDays(weekStart, delta * 7);
    setWeekStart(ws);
    setSelected(delta === 0 ? today : ws);
  };
  const goToday = () => {
    setWeekStart(mondayOf(today));
    setSelected(today);
  };

  const isToday = selected === today;
  const dayRef = isToday ? "hoje" : `${ddmm(selected)}`;
  const suggestions = [
    { label: `➕ Novo ${isToday ? "hoje" : `em ${ddmm(selected)}`}`, message: `Anota para ${isToday ? "hoje" : ddmm(selected)} às `, send: false },
    { label: `📋 O que tenho ${dayRef}?`, message: `O que eu tenho na agenda ${isToday ? "hoje" : `no dia ${ddmm(selected)}`}?`, send: true },
    { label: "🗓️ Resumo da semana", message: `Me mostra meus compromissos de ${ddmm(weekStart)} até ${ddmm(weekEnd)}.`, send: true },
    { label: "⏰ Próximo compromisso", message: "Qual é o meu próximo compromisso?", send: true },
    { label: "🔁 Remarcar", message: "Quero remarcar o compromisso ", send: false },
  ];

  const nowMs = Date.now();
  const nowIndex = isToday ? dayEvents.findIndex((e) => new Date(e.startsAt).getTime() > nowMs) : -1;

  return (
    <div className="ai-chat-bg relative flex min-h-0 flex-1 flex-col">
      {/* Calendário semanal */}
      <div className="border-b ai-border bg-white px-3 pb-3 pt-2 shadow-[0_6px_18px_-14px_rgba(15,23,42,0.5)] dark:border-slate-800 dark:bg-slate-900">
        <div className="mb-2 flex items-center gap-1">
          <button type="button" aria-label="Semana anterior" onClick={() => goWeek(-1)} className="grid h-8 w-8 place-items-center rounded-full hover:bg-slate-100 dark:hover:bg-slate-800">
            <ChevronLeft className="h-5 w-5 text-slate-600 dark:text-slate-300" />
          </button>
          <button
            type="button"
            onClick={() => {
              setCalendarMonth(selected.slice(0, 7));
              setCalendarOpen(true);
            }}
            className="flex-1 rounded-xl py-1 text-center text-sm font-bold text-slate-800 hover:bg-slate-50 dark:text-slate-100 dark:hover:bg-slate-800"
          >
            {monthLabel} <span className="ai-text">▾</span>
          </button>
          <button type="button" aria-label="Próxima semana" onClick={() => goWeek(1)} className="grid h-8 w-8 place-items-center rounded-full hover:bg-slate-100 dark:hover:bg-slate-800">
            <ChevronRight className="h-5 w-5 text-slate-600 dark:text-slate-300" />
          </button>
          {!(weekStart === mondayOf(today) && isToday) && (
            <button type="button" onClick={goToday} className="ml-1 rounded-full ai-soft px-3 py-1 text-xs font-bold ai-text">
              Hoje
            </button>
          )}
          <button
            type="button"
            onClick={() => setEditor({ event: null })}
            className="ml-1 inline-flex items-center gap-1 rounded-full ai-grad px-3 py-1.5 text-xs font-bold shadow-md active:scale-95"
          >
            <Plus className="h-3.5 w-3.5" /> Novo
          </button>
        </div>
        <div className="grid grid-cols-7 gap-1">
          {days.map((d, i) => {
            const active = d === selected;
            const count = byDay.get(d)?.length ?? 0;
            return (
              <button
                key={d}
                type="button"
                onClick={() => setSelected(d)}
                aria-pressed={active}
                className={cn(
                  "flex flex-col items-center rounded-2xl py-2 transition active:scale-95",
                  active ? "ai-grad shadow-md" : "hover:bg-slate-50 dark:hover:bg-slate-800",
                  !active && d === today && "ring-2 ring-inset ai-border",
                )}
              >
                <span className={cn("text-[10px] font-semibold uppercase", active ? "opacity-90" : "text-slate-400")}>{WEEKDAYS[i]}</span>
                <span className={cn("text-lg font-extrabold leading-tight", !active && (d === today ? "ai-text" : "text-slate-800 dark:text-slate-100"), d < today && !active && "opacity-50")}>
                  {dayNum(d)}
                </span>
                <span className="mt-0.5 flex h-1.5 gap-0.5">
                  {Array.from({ length: Math.min(count, 3) }, (_, k) => (
                    <i key={k} className={cn("block h-1.5 w-1.5 rounded-full", active ? "bg-white" : "bg-[var(--ai)]")} />
                  ))}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Linha do tempo do dia */}
      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
        {next && (
          <div className="mb-3 flex items-center gap-3 rounded-3xl ai-grad p-3.5 shadow-[0_12px_26px_-14px_rgba(15,23,42,0.7)]">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-white/20 ring-1 ring-white/30">
              <Clock className="h-5 w-5" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-[11px] font-bold uppercase tracking-wider opacity-80">Próximo · {untilLabel(next.startsAt)}</div>
              <div className="truncate font-bold">{next.title}</div>
              <div className="text-xs opacity-85">
                {ymdOf(next.startsAt) === today ? "Hoje" : longDay(ymdOf(next.startsAt))} às {hhmm(next.startsAt)}
              </div>
            </div>
          </div>
        )}

        <div className="mb-2 flex items-baseline justify-between">
          <h3 className="text-[15px] font-extrabold text-slate-800 dark:text-slate-100">{isToday ? "Hoje" : longDay(selected)}</h3>
          <span className="text-xs font-medium text-slate-500">
            {dayEvents.length ? `${dayEvents.length} compromisso${dayEvents.length > 1 ? "s" : ""}` : ""}
          </span>
        </div>

        {events.isLoading && <Loader2 className="mx-auto my-8 h-5 w-5 animate-spin text-slate-400" />}
        {events.error && <p className="rounded-2xl bg-red-50 p-3 text-sm text-red-700">Não foi possível carregar a agenda.</p>}

        {!events.isLoading && dayEvents.length === 0 && (
          <div className="flex flex-col items-center rounded-3xl border border-dashed ai-border bg-white/70 px-6 py-8 text-center dark:bg-slate-900/60">
            <span className="grid h-14 w-14 place-items-center rounded-2xl ai-soft text-2xl">🌿</span>
            <div className="mt-3 font-bold text-slate-800 dark:text-slate-100">Dia livre</div>
            <p className="mt-1 text-sm text-slate-500">Diga ou escreva para a IA — ex.: “dentista às 9h, me lembra 30 min antes”.</p>
            <button
              type="button"
              onClick={() => setEditor({ event: null })}
              className="mt-4 inline-flex items-center gap-2 rounded-full ai-grad px-4 py-2 text-sm font-bold shadow-md active:scale-95"
            >
              <CalendarPlus className="h-4 w-4" /> Anotar compromisso
            </button>
          </div>
        )}

        <ol className="relative space-y-3">
          {dayEvents.length > 0 && <span className="absolute bottom-2 left-[52px] top-2 w-0.5 rounded-full bg-[var(--ai-border)]" aria-hidden />}
          {dayEvents.map((e, i) => {
            const past = new Date(e.startsAt).getTime() < nowMs;
            return (
              <li key={e.id}>
                {i === nowIndex && <NowMarker />}
                <div className={cn("flex gap-3", past && "opacity-55")}>
                  <div className="w-11 shrink-0 pt-3 text-right text-[13px] font-bold tabular-nums text-slate-700 dark:text-slate-200">{hhmm(e.startsAt)}</div>
                  <span className={cn("relative z-10 mt-4 h-3 w-3 shrink-0 rounded-full ring-4 ring-white dark:ring-slate-950", past ? "bg-slate-300" : "bg-[var(--ai)]")} />
                  <div className="min-w-0 flex-1 rounded-2xl border border-white/70 bg-white p-3 shadow-[0_8px_22px_-16px_rgba(15,23,42,0.55)] dark:border-slate-800 dark:bg-slate-900">
                    <div className="font-bold text-slate-900 dark:text-white">{e.title}</div>
                    {e.notes && <div className="mt-0.5 text-sm text-slate-500">{e.notes}</div>}
                    <div className="mt-2 flex flex-wrap items-center gap-1.5">
                      {e.remindMinutesBefore != null && (
                        <span className="inline-flex items-center gap-1 rounded-full ai-soft px-2 py-0.5 text-[11px] font-semibold ai-text">
                          <Bell className="h-3 w-3" /> {remindLabel(e.remindMinutesBefore)}
                        </span>
                      )}
                      <span className="flex-1" />
                      {confirmDelete === e.id ? (
                        <>
                          <button
                            type="button"
                            onClick={() => remove.mutate(e.id)}
                            disabled={remove.isPending}
                            className="rounded-full bg-red-600 px-3 py-1 text-xs font-bold text-white disabled:opacity-60"
                          >
                            {remove.isPending ? "Cancelando…" : "Confirmar cancelamento"}
                          </button>
                          <button type="button" onClick={() => setConfirmDelete(null)} className="rounded-full px-2 py-1 text-xs text-slate-500">
                            Voltar
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={() => setEditor({ event: e })}
                            className="inline-flex items-center gap-1 rounded-full border ai-border px-2.5 py-1 text-xs font-semibold ai-text"
                          >
                            <Pencil className="h-3 w-3" /> Editar
                          </button>
                          <button
                            type="button"
                            onClick={() => setConfirmDelete(e.id)}
                            className="inline-flex items-center gap-1 rounded-full border border-red-200 px-2.5 py-1 text-xs font-semibold text-red-600 dark:border-red-900"
                          >
                            <Trash2 className="h-3 w-3" /> Cancelar
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
          {isToday && dayEvents.length > 0 && nowIndex === -1 && <NowMarker />}
        </ol>
        {remove.error && <p className="mt-2 text-sm text-red-600">Não foi possível cancelar. Tente de novo.</p>}
      </div>

      {/* IA da agenda (texto ou voz) */}
      <div className="border-t ai-border bg-white/95 dark:border-slate-800 dark:bg-slate-950/95">
        <AssistantChat
          me={me}
          compact
          conversationId={conversationId}
          onConversationChange={setConversationId}
          suggestions={suggestions}
          prefill={prefill}
          placeholder="Peça à IA: “reunião amanhã às 14h”…"
          bottomInset={keyboardInset}
          compactBottom
        />
      </div>
      {calendarOpen && (
        <div className="absolute inset-0 z-50 flex flex-col bg-black/40" onClick={() => setCalendarOpen(false)}>
          <div
            className="ai-in mt-auto rounded-t-[28px] bg-white px-4 pb-6 pt-3 dark:bg-slate-900"
            style={{ paddingBottom: "calc(1.5rem + env(safe-area-inset-bottom))" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-1 flex justify-end">
              <button type="button" aria-label="Fechar calendário" onClick={() => setCalendarOpen(false)} className="grid h-8 w-8 place-items-center rounded-full hover:bg-slate-100 dark:hover:bg-slate-800">
                <X className="h-5 w-5 text-slate-500" />
              </button>
            </div>
            <CalendarPicker
              value={selected}
              today={today}
              marked={markedDays}
              onVisibleMonthChange={setCalendarMonth}
              onChange={(d) => {
                jumpTo(d);
                setCalendarOpen(false);
              }}
            />
            <button
              type="button"
              onClick={() => {
                setCalendarOpen(false);
                setEditor({ event: null });
              }}
              className="mt-4 flex w-full items-center justify-center gap-2 rounded-2xl ai-grad py-3 text-sm font-bold shadow-md active:scale-[0.98]"
            >
              <CalendarPlus className="h-4 w-4" /> Novo compromisso em {ddmm(selected)}
            </button>
          </div>
        </div>
      )}

      {editor && (
        <EventEditor
          event={editor.event}
          initialDate={selected < today ? today : selected}
          today={today}
          marked={markedDays}
          onClose={() => setEditor(null)}
          onSaved={jumpTo}
        />
      )}
    </div>
  );
}

function NowMarker() {
  return (
    <div className="my-1 flex items-center gap-2 pl-1">
      <span className="rounded-full bg-red-500 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">Agora</span>
      <span className="h-px flex-1 bg-red-300" />
    </div>
  );
}
